import { T, useGT } from 'gt-react';
import type { TaskResponse, TaskTypeResponse } from '@erp/rpc';
import { Card } from 'src/components/ui/card';
import { TaskHelpRequestCard } from 'src/components/tasks/TaskHelpRequestCard';
import { TaskInputCard } from './TaskInputCard';
import { SubtaskList } from './SubtaskList';
import { TaskTypeViewRenderer } from './view/TaskTypeView';
import { showsTaskInputCard } from './view/taskTypeViewValue';
import type { TaskViewState } from './view/useTaskViewState';

/**
 * Left column of the task review page: help, the type's view, then the
 * description and subtasks. The raw input card renders only when the type
 * has no view.
 */
export function TaskReviewMainColumn({
  task,
  taskType,
  view,
  subtasks,
  typeNameById,
  fromPage,
  agentNameById,
  onContinueInChat,
}: {
  task: TaskResponse;
  taskType: TaskTypeResponse | undefined;
  view: TaskViewState;
  subtasks: TaskResponse[];
  typeNameById: Map<string, string>;
  fromPage?: string;
  agentNameById: Map<string, string>;
  onContinueInChat: () => void;
}) {
  const gt = useGT();
  const { slot } = view;
  const waitingSubtasks = subtasks.filter(
    (subtask) => subtask.status === 'needs_input',
  );

  return (
    <>
      <TaskHelpRequestCard
        task={task}
        agentNameById={agentNameById}
        onContinueInChat={onContinueInChat}
      />
      {waitingSubtasks.map((subtask) => (
        <TaskHelpRequestCard
          key={subtask.id}
          task={subtask}
          agentNameById={agentNameById}
        />
      ))}

      {slot.kind === 'ready' ? <TaskViewBlock view={view} /> : null}
      {slot.kind === 'empty' ? (
        <Card className="p-3" data-testid="task-type-view-empty">
          <p className="text-sm text-stone-800">
            {gt('This view could not be shown.', {
              context:
                'empty state when a task type view spec cannot be parsed',
            })}
          </p>
          {slot.reason === 'invalid' && slot.detail ? (
            <p className="mt-1 text-xs text-muted-foreground">{slot.detail}</p>
          ) : null}
        </Card>
      ) : null}

      {task.description ? (
        <Card className="p-3">
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-stone-500">
            <T context="heading for the task description">Description</T>
          </div>
          <div className="whitespace-pre-wrap text-sm text-stone-800">
            {task.description}
          </div>
        </Card>
      ) : null}

      <SubtaskList
        subtasks={subtasks}
        typeNameById={typeNameById}
        fromPage={fromPage}
      />

      {showsTaskInputCard(slot) ? (
        <TaskInputCard input={task.input} taskType={taskType} />
      ) : null}
    </>
  );
}

/** The type's main regions, inert while a supplier change rematches rows. */
function TaskViewBlock({ view }: { view: TaskViewState }) {
  return (
    <div
      inert={view.rematching}
      aria-busy={view.rematching}
      className={view.rematching ? 'opacity-70' : undefined}
    >
      <TaskTypeViewRenderer
        regions={view.mainRegions}
        schemaFields={view.schemaFields}
        input={view.input}
        workingCopy={view.workingCopy}
        anchorType={view.anchorType}
        anchorId={view.anchorId}
        onCommit={view.commit}
      />
    </div>
  );
}
